import { useState } from 'react';
import styled from 'styled-components';
import { Attachment } from '../../graphql/graphql';

const AttachmentLink = styled.a`
  display: block;
  max-width: 100%;
  color: inherit;
  overflow-wrap: anywhere;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
`;

const AttachmentImage = styled.img`
  display: block;
  width: 280px;
  max-width: 100%;
  height: auto;
  aspect-ratio: 1;
  object-fit: contain;
  border-radius: 10px;
`;

const AttachmentStatus = styled.span`
  display: block;
  padding: 4px 0;
`;

interface Props {
  attachment: Attachment;
}

const MessageAttachment = ({ attachment }: Props) => {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const { contentType, deleted, expired, thumbnailUrl, uploadComplete, url } = attachment;

  if (deleted || expired) {
    return <AttachmentStatus>{expired ? 'Attachment expired' : 'Attachment deleted'}</AttachmentStatus>;
  }

  if (!uploadComplete) {
    return <AttachmentStatus>Attachment upload pending</AttachmentStatus>;
  }

  if (!url) {
    return <AttachmentStatus>Attachment unavailable</AttachmentStatus>;
  }

  if (contentType.startsWith('image/')) {
    const source = thumbnailUrl && failedSource !== thumbnailUrl ? thumbnailUrl : url;

    if (failedSource === url) {
      return <AttachmentStatus>Image unavailable</AttachmentStatus>;
    }

    return (
      <AttachmentLink href={url} target="_blank" rel="noopener noreferrer" aria-label="Open full-size image">
        <AttachmentImage
          src={source}
          alt="Image attachment"
          loading="lazy"
          decoding="async"
          onError={() => setFailedSource(source)}
        />
      </AttachmentLink>
    );
  }

  const label = contentType.startsWith('video/')
    ? 'Video attachment'
    : contentType.startsWith('audio/') ? 'Audio attachment' : 'File attachment';

  return <AttachmentLink href={url} target="_blank" rel="noopener noreferrer">{label}</AttachmentLink>;
};

export default MessageAttachment;
